import { useCallback, useEffect, useRef, useState } from "react"
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useField } from "formik"
import { FormControlLabel, FormHelperText, IconButton, Stack, Switch, Typography } from "@mui/material"

import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../../modules/localization/components/AppLocalizationProvider"
import { useEnvironment } from "../../../../modules/environment/envHooks"
import {
    BookingMessageType,
    requestOutlookAttendees,
    subscribeToParentMessages,
} from "../../../../modules/order/pages/components/bookingMessaging"
import { useOutlookAttendeesOptIn } from "../OutlookAttendeesOptInContext"
import { AttendeeSource, isOutlookSourced, reconcileImportedAttendees } from "../outlookAttendees"

/** How long to wait for OUTLOOK_ATTENDEES_PROVIDE before giving up on an add-in that cannot answer. */
const ATTENDEES_REQUEST_TIMEOUT_MS = 8000

type TaggedAttendee = Readonly<{
    name: string
    company: string
    isExternal: boolean
    source?: AttendeeSource
}>

type ShareOutlookAttendeesToggleProps = Readonly<{
    /** Formik field holding the participants list (REGISTER_PARTICIPANTS for service, ADD_PARTICIPANTS_FORM for booking). */
    fieldKey: string
    /** Read-only mode: the current list stays visible, but it cannot be imported, removed or turned off. */
    disabled?: boolean
}>

/**
 * Outlook-only opt-in: lets the user pull the Outlook meeting attendees into the order at the confirm step.
 * Rendered only for the Outlook web flow and only when the meeting actually has attendees (non-PII count).
 * Nothing is requested until the user turns it on — the attendees are delivered on demand via postMessage.
 * Once added, each imported participant is listed and can be removed individually.
 *
 * In edit mode, when the order was previously saved with Outlook attendees (`initialShare`), the toggle
 * starts on and eagerly reconciles the saved participants against the *current* Outlook meeting (auto-sync),
 * so re-opening never duplicates and the user can revert (turn off) the whole addition.
 */
export function ShareOutlookAttendeesToggle({ fieldKey, disabled = false }: ShareOutlookAttendeesToggleProps) {
    const { l10n } = useAppLocalization()
    const { currentEnv } = useEnvironment()
    const optIn = useOutlookAttendeesOptIn()
    const [{ value }, , helpers] = useField<TaggedAttendee[]>(fieldKey)

    const [isLoading, setIsLoading] = useState(false)

    // Everything Outlook-specific comes from the opt-in context (null outside the create-order Outlook flow),
    // so this component is safe to render inside the shared booking dialog even when used elsewhere.
    const share = optIn?.share ?? false
    const isOutlookWebFlow = optIn?.isOutlookWebFlow ?? false
    const count = optIn?.outlookAttendeeCount ?? 0
    const parentOrigin = new URLSearchParams(window.location.search).get("parentOrigin") || "*"

    const setFieldRef = useRef(helpers.setValue)
    setFieldRef.current = helpers.setValue
    const valueRef = useRef<TaggedAttendee[]>(value ?? [])
    valueRef.current = value ?? []
    const optInRef = useRef(optIn)
    optInRef.current = optIn
    // Names of the last-imported Outlook attendees (normalized). Used as a robust fallback for removal in
    // case the UI-only `source` tag is stripped by the service form's reinitialize/sync pipeline.
    const importedNamesRef = useRef<Set<string>>(new Set())
    const importTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

    const setShare = useCallback((next: boolean) => {
        optInRef.current?.setShare(next)
        if (optInRef.current) optInRef.current.shareRef.current = next
    }, [])

    const clearImportTimeout = useCallback(() => {
        if (importTimeoutRef.current === undefined) return
        clearTimeout(importTimeoutRef.current)
        importTimeoutRef.current = undefined
    }, [])

    // The headcount now also comes from an add-in that may be too old to answer the handshake, so a request
    // can go unanswered. Give up instead of leaving the switch on and stuck in its loading state.
    const armImportTimeout = useCallback(
        (revertShare: boolean) => {
            clearImportTimeout()
            importTimeoutRef.current = setTimeout(() => {
                importTimeoutRef.current = undefined
                setIsLoading(false)
                if (revertShare) setShare(false)
            }, ATTENDEES_REQUEST_TIMEOUT_MS)
        },
        [clearImportTimeout, setShare],
    )

    useEffect(() => clearImportTimeout, [clearImportTimeout])

    // An attendee counts as Outlook-imported if it carries the `source` tag OR its name matches a
    // just-imported one. The name fallback is needed because the service form's reinitialize/sync pipeline
    // strips the UI-only `source` tag, which would otherwise break removal and the imported list display.
    const isImported = useCallback(
        (attendee: TaggedAttendee) =>
            isOutlookSourced(attendee) || importedNamesRef.current.has(attendee.name.trim().toLowerCase()),
        [],
    )

    useEffect(() => {
        // Only register message handlers for the actual Outlook web flow with attendees — avoids adding
        // window/Office listeners (and postMessage attack surface) across every non-Outlook usage.
        const oi = optInRef.current
        if (!oi?.isOutlookWebFlow || oi.outlookAttendeeCount <= 0) return

        const unsubscribe = subscribeToParentMessages((msg) => {
            if (msg.type !== BookingMessageType.OUTLOOK_ATTENDEES_PROVIDE) return
            // Ignore late responses if the user turned the opt-in back off.
            if (!optInRef.current?.shareRef.current) return
            // Reconcile against the current Outlook meeting: dedupe by name (saved attendees carry no
            // provenance), drop stale tagged/placeholder rows, append the fresh tagged Outlook attendees.
            const reconciled = reconcileImportedAttendees(valueRef.current, msg.attendees)
            importedNamesRef.current = new Set(msg.attendees.map((a) => a.name.trim().toLowerCase()))
            setFieldRef.current(reconciled)
            clearImportTimeout()
            setIsLoading(false)
        })
        return unsubscribe
    }, [])

    useEffect(() => {
        // Edit mode: if this order was previously saved with Outlook attendees, eagerly pull the current
        // Outlook list once to reconcile (tag/refresh) the saved participants. They are already on the
        // backend, so this is not a new disclosure.
        if (!isOutlookWebFlow || count <= 0) return
        if (optInRef.current?.isEdit && optInRef.current?.share && !valueRef.current.some(isImported)) {
            setIsLoading(true)
            // Keep the saved opt-in on timeout: the participants are already on the order, and turning it
            // off here would silently revoke the choice recorded on the previous save.
            armImportTimeout(false)
            requestOutlookAttendees(parentOrigin, currentEnv).catch((e) => {
                console.error("Failed to request Outlook attendees", e)
                clearImportTimeout()
                setIsLoading(false)
            })
        }
    }, [])

    const handleToggle = useCallback(
        (next: boolean) => {
            setShare(next)
            if (next) {
                // Reset the remembered names before a fresh import so the auto-off effect doesn't fire
                // prematurely while the request is in flight.
                importedNamesRef.current = new Set()
                setIsLoading(true)
                armImportTimeout(true)
                requestOutlookAttendees(parentOrigin, currentEnv).catch((e) => {
                    console.error("Failed to request Outlook attendees", e)
                    clearImportTimeout()
                    setIsLoading(false)
                    setShare(false)
                })
            } else {
                // Remove Outlook-imported attendees (by tag or imported name); keep manual ones.
                // Keep `importedNamesRef` so the self-heal effect can catch any re-added (tag-stripped) ones.
                setFieldRef.current(valueRef.current.filter((a) => !isImported(a)))
            }
        },
        [parentOrigin, currentEnv, setShare, isImported, armImportTimeout, clearImportTimeout],
    )

    const handleRemove = useCallback(
        (attendee: TaggedAttendee) => {
            // Match by value, not object reference: the service form reinitializes/normalizes values, so the
            // clicked object may not be the same instance as the one currently in the field.
            const name = attendee.name.trim().toLowerCase()
            const company = (attendee.company ?? "").trim().toLowerCase()
            const next = valueRef.current.filter(
                (a) => a.name.trim().toLowerCase() !== name || (a.company ?? "").trim().toLowerCase() !== company,
            )
            importedNamesRef.current.delete(name)
            setFieldRef.current(next)
            // When the last Outlook-imported participant is removed, flip the opt-in back off.
            if (!next.some(isImported)) {
                setShare(false)
            }
        },
        [setShare, isImported],
    )

    // The service form's reinitialize/sync can re-add just-removed imports from stale outer values. While the
    // opt-in is off, keep the field free of Outlook-imported attendees so removal actually sticks.
    useEffect(() => {
        if (share) return
        const current = value ?? []
        if (current.some(isImported)) {
            setFieldRef.current(current.filter((a) => !isImported(a)))
        }
    }, [share, value, isImported])

    // Turn the opt-in off automatically once the last Outlook-imported participant is gone — including
    // removals done in the "Edit participants" modal, which the toggle's own remove button doesn't cover.
    // Guarded by `importedNamesRef` so it doesn't fire while a fresh import request is still in flight.
    useEffect(() => {
        if (!share || importedNamesRef.current.size === 0) return
        if (!(value ?? []).some(isImported)) {
            setShare(false)
        }
    }, [share, value, isImported, setShare])

    if (!isOutlookWebFlow || count <= 0) return null

    const imported = (value ?? []).filter(isImported)
    const showImported = share && imported.length > 0

    return (
        <Stack gap=".5rem" alignItems="flex-start">
            <LocalizedStrict id="create-order-share-outlook-attendees-toggle-label" attrs={{ label: true }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={share}
                            onChange={(e) => handleToggle(e.target.checked)}
                            disabled={isLoading || disabled}
                        />
                    }
                    label="Add participants from Outlook"
                />
            </LocalizedStrict>

            {showImported ? (
                <>
                    <LocalizedStrict
                        id="create-order-share-outlook-attendees-toggle-helper-on"
                        vars={{ count: imported.length.toString() }}
                    >
                        <FormHelperText>
                            {imported.length} participant(s) added from your Outlook meeting.
                        </FormHelperText>
                    </LocalizedStrict>
                    <Stack gap=".25rem" alignItems="flex-start">
                        {imported.map((attendee, index) => (
                            <Stack
                                key={`${attendee.name}-${attendee.company}-${index}`}
                                direction="row"
                                alignItems="center"
                                gap=".5rem"
                            >
                                <Typography variant="body2">
                                    {attendee.company ? `${attendee.name} (${attendee.company})` : attendee.name}
                                </Typography>
                                <IconButton
                                    size="small"
                                    aria-label={l10n.getString("create-order-share-outlook-attendees-remove")}
                                    onClick={() => handleRemove(attendee)}
                                    disabled={disabled}
                                >
                                    <FontAwesomeIcon icon={faTrashCan} size="xs" />
                                </IconButton>
                            </Stack>
                        ))}
                    </Stack>
                </>
            ) : (
                <LocalizedStrict
                    id="create-order-share-outlook-attendees-toggle-helper"
                    vars={{ count: count.toString() }}
                >
                    <FormHelperText>
                        Turning this on adds {count} participant(s) from your Outlook meeting.
                    </FormHelperText>
                </LocalizedStrict>
            )}
        </Stack>
    )
}
